import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pin, MessageCircle, Loader2, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Container, GoldButton } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";

export default function ForumBoard() {
  const { sector } = useParams();
  const decoded = decodeURIComponent(sector);
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/forum/threads", { params: { sector: decoded } }).then((r) => setThreads(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [decoded]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/forum/threads", { sector: decoded, ...form });
      toast.success("Thread posted.");
      setForm({ title: "", body: "" });
      setShowForm(false);
      navigate(`/forum/thread/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!ready) return <div className="py-40 flex justify-center"><Loader2 className="animate-spin text-[#CDA434]" size={32} /></div>;

  return (
    <div>
      <section className="bg-[#1E1E2F] text-[#FFFAF3] border-b border-[#D6D3CB]">
        <Container className="py-12">
          <Link to="/forum" className="inline-flex items-center gap-1 text-[#FFFAF3]/70 hover:text-[#FFFAF3] text-sm mb-4 link-underline">
            <ArrowLeft size={14} /> All trade rooms
          </Link>
          <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#CDA434] mb-2">Trade room</p>
          <h1 className="font-serif text-3xl sm:text-4xl">{decoded}</h1>
        </Container>
      </section>

      <section className="py-12">
        <Container>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-xl text-[#1E1E2F]">Threads</h2>
            {user ? (
              <GoldButton data-testid="new-thread-btn" onClick={() => setShowForm((s) => !s)} className="px-4 py-2">
                <Plus size={15} className="mr-1.5" /> New thread
              </GoldButton>
            ) : (
              <GoldButton as="link" to="/signin" className="px-4 py-2">Sign in to post</GoldButton>
            )}
          </div>

          {showForm && (
            <form onSubmit={submit} data-testid="new-thread-form" className="bg-[#FFFAF3] border border-[#D6D3CB] p-6 mb-8 space-y-4">
              <div>
                <label className={labelCls}>Title</label>
                <input required data-testid="thread-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Your message</label>
                <textarea required rows={4} data-testid="thread-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${inputCls} resize-none`} />
              </div>
              <GoldButton type="submit" disabled={busy} data-testid="thread-submit">
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Post thread"}
              </GoldButton>
            </form>
          )}

          {loading ? (
            <div className="h-40 bg-[#FFFAF3] border border-[#D6D3CB] animate-pulse" />
          ) : threads.length === 0 ? (
            <p className="font-sans text-sm text-[#505A68] py-10 text-center border border-dashed border-[#D6D3CB]">No threads here yet. Start the first.</p>
          ) : (
            <div className="space-y-3" data-testid="thread-list">
              {threads.map((t) => (
                <Link key={t.id} to={`/forum/thread/${t.id}`} data-testid={`thread-${t.id}`}
                  className="group flex items-start gap-4 bg-[#FFFAF3] border border-[#D6D3CB] hover:border-[#CDA434] transition-colors p-5">
                  {t.pinned && <Pin size={16} className="text-[#CDA434] mt-1 shrink-0" />}
                  <div className="flex-1">
                    <h3 className="font-serif text-lg text-[#1E1E2F] group-hover:text-[#003366] transition-colors">
                      {t.pinned && <span className="text-xs uppercase tracking-wide text-[#7a611c] mr-2">Pinned</span>}
                      {t.title}
                    </h3>
                    <p className="font-sans text-sm text-[#505A68] line-clamp-1 mt-1">{t.body}</p>
                    <p className="font-sans text-xs text-[#505A68] mt-2">
                      {t.author_name} · {new Date(t.created_at).toLocaleDateString("en-GB")}
                    </p>
                  </div>
                  <span className="flex items-center gap-1 text-sm text-[#505A68]"><MessageCircle size={14} /> {t.reply_count}</span>
                </Link>
              ))}
            </div>
          )}
        </Container>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-[#FFFAF3] border border-[#D6D3CB] focus:border-[#003366] px-4 py-2.5 text-sm font-sans text-[#1B2430]";
const labelCls = "block font-sans text-xs uppercase tracking-wider font-semibold text-[#1B2430] mb-1.5";
